import React, { useState, useRef, useMemo } from 'react';
import { format } from 'date-fns';
import pt from 'date-fns/locale/pt';
import { useSelector } from 'react-redux';
import { SimpleTreeView } from '@mui/x-tree-view';
import useSWR from 'swr';
import {
  Col,
  Card,
  Form,
  Button,
  ButtonGroup,
  Spinner,
  Modal,
} from 'react-bootstrap';
import QRCode from 'qrcode';
import { FaArrowRight, FaAngleDown, FaWhatsapp } from 'react-icons/fa';
import { useReactToPrint } from 'react-to-print';
import { toast } from 'react-toastify';
import OrientacaoMarevan from '../prontuario/OrientacaoMarevan';
import OrientacaoDOC from '../prontuario/OrientacaoDOC';
import { updateReceita, insertReceita } from '../../services/prontuario';
import { medicamentos as dbmedicamentos, getPrescricao } from '../../helpers/prontuario/medicamentos';
import { removeBeforeBase64, openBase64Pdf } from '../../util/utils';
import api from '../../services/api';
import getPacienteById from '../../services/paciente';
import gerarPdfReceita from './gerarPdfReceita';
import { signDocument, getVidaasStatus } from '../../services/vidaasV2';
import {
  normalizeWhatsappPhone,
  enviarArquivo,
  getPdfBase64,
} from '../../services/pdfWhatsapp';

export default function ReceituarioMedico({
  closeModal,
  codPaciente,
  codConsulta = null,
  renderTreeMedicamento = null,
  assinaturaDigital = null,
  setAssinaturaDigital = null,
}) {
  const orientacaoRef = useRef();
  const orientacaoDOCRef = useRef();
  const medico = useSelector(state => state.doctor.profile);
  const printOrientacaoMarevan = useReactToPrint({
    contentRef: orientacaoRef,
  });
  const printOrientacaoDOC = useReactToPrint({
    contentRef: orientacaoDOCRef,
  });
  const [assinaturaEletronica, setAssinaturaEletronica] = useState(false);
  const [receita, setReceita] = useState(null);
  const [usoReceita, setUsoReceita] = useState('oral-continuo');
  const [signing, setSigning] = useState(false);
  const [receitaCounter, setReceitaCounter] = useState(1);
  const [isSavingReceita, setIsSavingReceita] = useState(false);
  const [tipoReceitaPreferido, setTipoReceitaPreferido] = useState('Normal');
  const [showModalRepetir, setShowModalRepetir] = useState(false);
  const [tipoRepeticao, setTipoRepeticao] = useState('Normal');
  const isDoctorLoggedIn = !!medico;
  const { data: receitasData, mutate: mutateReceitas } = useSWR(
    codPaciente ? `receitas-${codPaciente}` : null,
    () =>
      api
        .get(`paciente/${codPaciente}/receitas`)
        .then(result => result?.data)
        .catch(e => {
          toast.error(e?.response?.data?.error || 'Erro ao carregar receitas');
          throw e;
        })
  );

  const { data } = useSWR(codPaciente ? `paciente-${codPaciente}` : null, () =>
    getPacienteById(codPaciente)
  );
  const paciente = data?.length > 0 ? data[0] : null;
  const receitas = receitasData || [];

  // Retorna a URL adequada para o QR Code dependendo do tipo de assinatura (Vidaas / ITI vs Interno)
  const getQrCodeUrl = (r) => {
    if (assinaturaDigital) {
      return 'https://validar.iti.gov.br/';
    }
    return `https://icm-marilia.com/receita/validacao?receita=${r.codReceita}&paciente=${r.codPaciente}`;
  };

  // Derived state: current receita (selected or first available)
  const currentReceita = useMemo(() => {
    if (receita) return receita;
    if (receitas.length > 0) {
      return {
        codReceita: receitas[0].CODRECEITA,
        codPaciente: receitas[0].CODPAC,
        codMedico: receitas[0].CODMEDICO,
        nomeMedico: receitas[0].NOMEMED,
        crm: receitas[0].CRMMED,
        cpf: receitas[0].CPFMED,
        rqe: receitas[0].RQE,
        nomePaciente: receitas[0].NOMEPAC,
        codConsulta: receitas[0].CODCONSULTA,
        dataRec: receitas[0].dataFormatada,
        tipo: receitas[0].TIPORECEITA,
        descricao: receitas[0].DESCRICAO,
        status: receitas[0].STATUS,
        endResPac: receitas[0].ENDRESPAC,
        numero: receitas[0].ENDERECO_NR,
        bairro: receitas[0].BAIRROPAC,
        cidade: receitas[0].CIDADEPAC,
        estado: receitas[0].ESTADOPAC,
      };
    }
    return null;
  }, [receita, receitas]);

  const tipoAtual = currentReceita?.tipo ?? tipoReceitaPreferido;

  const handleAlterarTipoReceita = novoTipo => {
    setTipoReceitaPreferido(novoTipo);
    if (currentReceita) {
      setReceita({ ...currentReceita, tipo: novoTipo });
    }
  };

  const handleGravarReceita = async () => {
    if (!currentReceita) return toast.warn('Selecione uma receita!');

    try {
      setIsSavingReceita(true);
      const res = await updateReceita(currentReceita);
      setReceitaCounter(1);
      await mutateReceitas();
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Erro ao gravar receita!');
    } finally {
      setIsSavingReceita(false);
    }
  };

  const handleNovaReceita = async () => {
    if (!codConsulta) {
      return toast.warn('Receita só pode ser gerada a partir do prontuário!');
    }

    try {
      const dt = format(new Date(), "dd 'de' MMMM 'de' yyyy", {
        locale: pt,
      });
      const endereco = `${paciente && paciente.ENDRESPAC}, ${paciente &&
        paciente.ENDERECO_NR} - ${paciente &&
        paciente.BAIRROPAC} - ${paciente && paciente.CIDADEPAC}/${paciente &&
        paciente.ESTADOPAC}`;
      const cabecalho = `
    Marília/SP, ${dt}.\n\n
    Nome: ${paciente && paciente.NOMEPAC} - CPF: ${paciente &&
        paciente.ENDMARPAC}
    Endereço: ${endereco}\n
    ${usoReceita === 'oral-continuo'
          ? 'USO ORAL CONTÍNUO'
          : 'USO EXTERNO CONTÍNUO'
        }\n`;

      const payload = {
        codConsulta,
        codMedico: medico.codMed,
        codPaciente,
        tipo: tipoAtual,
        descricao: cabecalho,
        status: 'ONLINE',
      };

      const response = await insertReceita(payload);

      setReceita({
        codReceita: response[0].CODRECEITA,
        codPaciente: response[0].CODPAC,
        codMedico: response[0].CODMEDICO,
        nomeMedico: response[0].NOMEMED,
        crm: response[0].CRMMED,
        cpf: response[0].CPFMED,
        rqe: response[0].RQE,
        nomePaciente: response[0].NOMEPAC,
        codConsulta: response[0].CODCONSULTA,
        dataRec: response[0].dataFormatada,
        tipo: response[0].TIPORECEITA,
        descricao: response[0].DESCRICAO,
        status: response[0].STATUS,
        endResPac: response[0].ENDRESPAC,
        numero: response[0].ENDERECO_NR,
        bairro: response[0].BAIRROPAC,
        cidade: response[0].CIDADEPAC,
        estado: response[0].ESTADOPAC,
      });
      setReceitaCounter(1);
      mutateReceitas();
      toast.success('Gerada nova receita. Selecione os medicamentos da lista!');
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Erro ao gerar receita!');
    }
  };

  const enviarPorWpp = async () => {
    if (!currentReceita) {
      toast.warn('Selecione uma receita!');
      return;
    }

    try {
      // Gera o QRCode base64 usando o link dinâmico (ITI se Vidaas ativo ou ICM)
      const qrCodeUrl = getQrCodeUrl(currentReceita);
      const qrcodeBase64 = await QRCode.toDataURL(qrCodeUrl, {
        type: 'image/jpeg',
        width: 120,
      });

      // Gera o PDF em base64
      const pdfGenerator = await gerarPdfReceita({
        receita: currentReceita,
        tipoImpressao: 'envioWpp',
        qrcodeBase64,
        assinaturaDigital: !!assinaturaDigital,
        assinaturaEletronica: !!assinaturaEletronica,
        isDoctorLoggedIn,
      });

      const celular = normalizeWhatsappPhone(paciente?.CELULARPAC);
      if (!celular) {
        toast.warn('Número de celular não encontrado!');
        return;
      }

      let pdfBase64;

      if (assinaturaDigital) {
        const unsignedBase64 = await getPdfBase64(pdfGenerator);
        const signedBase64 = await handleVidaasAssinar(
          `data:application/pdf;base64,${unsignedBase64}`
        );
        if (!signedBase64) return;
        pdfBase64 = signedBase64;
      } else {
        pdfBase64 = await getPdfBase64(pdfGenerator);
      }

      const data = await enviarArquivo({
        pdfBase64,
        to: celular,
        paciente: {
          id: currentReceita.codPaciente,
          nome: currentReceita.nomePaciente,
          medico: currentReceita.nomeMedico,
        },
        codLaudo: currentReceita.codReceita,
        fileName: `Receita_${currentReceita.codReceita}.pdf`,
        caption:
          currentReceita.tipo === 'Controlada'
            ? `Olá *${currentReceita.nomePaciente}*, tudo bem? 😊\nSegue em anexo o seu *Receituário de Controle Especial*.\nQualquer dúvida, nossa equipe estará à disposição pelo WhatsApp 14-3402-5252\nICM - Instituto do Coração de Marília`
            : `Olá *${currentReceita.nomePaciente}*, tudo bem? 😊\nSegue em anexo o seu *Receituário Médico*.\nQualquer dúvida, nossa equipe estará à disposição pelo WhatsApp 14-3402-5252\nICM - Instituto do Coração de Marília`,
        officialConfig: {
          procedimento: 'receita',
          documentTitle:
            currentReceita.tipo === 'Controlada'
              ? 'Receituário de Controle Especial'
              : 'Receituário Médico',
        },
      });

      toast.success(data.message);
    } catch (e) {
      toast.error(
        e?.response?.data?.error || 'Erro ao enviar receita por WhatsApp!'
      );
    }
  };

  // Gera/imprime receita e, se assinatura digital estiver ativa, assina via VIDAAS.
  const handleImprimirReceita = async (r, timbrado) => {
    if (!r) {
      toast.warn('Selecione uma receita!');
      return;
    }

    const getPdfDataUrl = pdfGenerator =>
      new Promise((resolve, reject) => {
        if (typeof pdfGenerator.getBase64 === 'function') {
          pdfGenerator.getBase64(base64 => {
            if (!base64) {
              reject(new Error('Falha ao gerar base64 da receita'));
              return;
            }
            resolve(`data:application/pdf;base64,${base64}`);
          });
          return;
        }

        if (typeof pdfGenerator.getBlob === 'function') {
          pdfGenerator.getBlob(blob => {
            const reader = new FileReader();
            reader.onloadend = () => {
              if (typeof reader.result !== 'string') {
                reject(new Error('Formato inesperado ao converter PDF'));
                return;
              }
              resolve(reader.result);
            };
            reader.onerror = () =>
              reject(new Error('Falha ao converter blob para base64'));
            reader.readAsDataURL(blob);
          });
          return;
        }

        reject(new Error('Gerador de PDF sem suporte a base64'));
      });

    try {
      // Gera o QRCode base64 usando o link dinâmico (ITI se Vidaas ativo ou ICM)
      const qrCodeUrl = getQrCodeUrl(r);
      const qrcodeBase64 = await QRCode.toDataURL(qrCodeUrl, {
        type: 'image/jpeg',
        width: 120,
      });

      const pdfGenerator = await gerarPdfReceita({
        receita: r,
        tipoImpressao: timbrado ? 'imprimirTimbrado' : 'imprimirNormal',
        qrcodeBase64,
        assinaturaDigital: !!assinaturaDigital,
        assinaturaEletronica: !!assinaturaEletronica,
        isDoctorLoggedIn,
      });

      if (assinaturaDigital) {
        const fileReceita = await getPdfDataUrl(pdfGenerator);
        const signedBase64 = await handleVidaasAssinar(fileReceita);
        if (signedBase64) openBase64Pdf(signedBase64);
        return;
      }

      pdfGenerator.open();
    } catch (e) {
      toast.error('Erro ao gerar PDF da receita!');
    }
  };

  const handleRepetirReceita = () => {
    if (!currentReceita) return toast.warn('Selecione a receita para repetir!');

    setTipoRepeticao(
      currentReceita.tipo === 'Controlada' ? 'Controlada' : 'Normal'
    );
    setShowModalRepetir(true);
    return undefined;
  };

  const handleConfirmarRepetirReceita = async tipoEscolhido => {
    if (!currentReceita) return;

    const tipoFinal = tipoEscolhido || tipoRepeticao || 'Normal';
    setShowModalRepetir(false);

    try {
      const dt = format(new Date(), "dd 'de' MMMM 'de' yyyy", {
        locale: pt,
      });
      const linhas = currentReceita?.descricao?.split('\n') || [];

      for (let i = 0; i < linhas.length; i++) {
        if (linhas[i].includes('Marília')) {
          linhas[i] = `Marília, ${dt}.`;
          break;
        }
      }

      const payload = {
        codConsulta: currentReceita.codConsulta,
        codMedico: medico ? medico.codMed : currentReceita.codMedico,
        codPaciente,
        tipo: tipoFinal,
        descricao: linhas.join('\n'),
        status: 'Clonada',
      };

      const response = await insertReceita(payload);
      setTipoReceitaPreferido(response[0].TIPORECEITA || tipoFinal);
      setReceita({
        codReceita: response[0].CODRECEITA,
        codPaciente: response[0].CODPAC,
        codMedico: response[0].CODMEDICO,
        nomeMedico: response[0].NOMEMED,
        crm: response[0].CRMMED,
        cpf: response[0].CPFMED,
        rqe: response[0].RQE,
        nomePaciente: response[0].NOMEPAC,
        codConsulta: response[0].CODCONSULTA,
        dataRec: response[0].dataFormatada,
        tipo: response[0].TIPORECEITA,
        descricao: response[0].DESCRICAO,
        status: response[0].STATUS,
        endResPac: response[0].ENDRESPAC,
        numero: response[0].ENDERECO_NR,
        bairro: response[0].BAIRROPAC,
        cidade: response[0].CIDADEPAC,
        estado: response[0].ESTADOPAC,
      });
      mutateReceitas();
      toast.success('Gerada nova receita. Selecione os medicamentos da lista!');
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Erro ao repetir receita!');
    }
  };

  const handleSelectMedicamentoReceita = (event, nodeId, elmID) => {
    dbmedicamentos.children.forEach(classe => {
      classe.children.forEach(e => {
        if (e.id === nodeId) {
          setReceitaCounter(receitaCounter + 1);
          setReceita({
            ...currentReceita,
            descricao:
              (currentReceita?.descricao || '') +
              '\n' +
              getPrescricao(e.name, receitaCounter),
          });
          document.getElementById(elmID).focus();
          event.stopPropagation();
        }
      });
    });
  };

  const handleVidaasAssinar = async content => {
    try {
      const vidaasStatus = await getVidaasStatus();
      if (!vidaasStatus?.authenticated) {
        toast.warn('Médico ainda não autenticado no VIDAAS!');
        return null;
      }

      const base64Strip = removeBeforeBase64(content);
      const payload = {
        documentType: 'receita',
        documentId: currentReceita.codReceita.toString(),
        alias: currentReceita.codReceita + '-receita.pdf',
        signatureFormat: 'CAdES_AD_RT',
        hashAlgorithm: '2.16.840.1.101.3.4.2.1',
        base64Content: base64Strip,
      };

      setSigning(true);
      const resp = await signDocument(payload);

      if (resp?.fileBase64Signed) {
        return resp.fileBase64Signed;
      }

      toast.warn('Assinatura não encontrada!');
      return null;
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Erro ao assinar receita!');
      return null;
    } finally {
      setSigning(false);
    }
  };

  return (
    <div style={{ display: 'flex' }}>
      <div style={{ flex: '0 0 10.5%' }}>
        {receitas.length > 0 ? (
          <ButtonGroup vertical>
            {receitas.map(rec => (
              <Button
                variant="outline-primary"
                key={rec.CODRECEITA}
                onClick={() => {
                  setTipoReceitaPreferido(rec.TIPORECEITA || 'Normal');
                  setReceita({
                    codReceita: rec.CODRECEITA,
                    codPaciente: rec.CODPAC,
                    codMedico: rec.CODMEDICO,
                    nomeMedico: rec.NOMEMED,
                    crm: rec.CRMMED,
                    cpf: rec.CPFMED,
                    rqe: rec.RQE,
                    nomePaciente: rec.NOMEPAC,
                    codConsulta: rec.CODCONSULTA,
                    dataRec: rec.dataFormatada,
                    tipo: rec.TIPORECEITA,
                    descricao: rec.DESCRICAO,
                    status: rec.STATUS,
                    endResPac: rec.ENDRESPAC,
                    numero: rec.ENDERECO_NR,
                    bairro: rec.BAIRROPAC,
                    cidade: rec.CIDADEPAC,
                    estado: rec.ESTADOPAC,
                  });
                }}
              >
                {rec.dataFormatada}
              </Button>
            ))}
          </ButtonGroup>
        ) : (
          <i className="text-danger">Paciente não tem receita!</i>
        )}
      </div>

      <Card className="p-1">
        <Form noValidate>
          <Form.Row>
            <Form.Group as={Col} sm controlId="editNomePAC">
              <strong>Paciente: {currentReceita?.nomePaciente}</strong>
            </Form.Group>
            <Form.Group as={Col} sm controlId="editNOMEMED">
              <strong>Médico: {currentReceita?.nomeMedico}</strong>
            </Form.Group>
          </Form.Row>
          <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            {isDoctorLoggedIn && (
              <Button size="sm" onClick={handleNovaReceita}>
                Nova Receita
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={handleRepetirReceita}
            >
              Repetir Receita
            </Button>
            <div>
              <Form.Check
                inline
                className="ml-5"
                label="Normal"
                checked={tipoAtual === 'Normal'}
                name="groupRec"
                type="radio"
                id="Normal"
                onChange={() => handleAlterarTipoReceita('Normal')}
              />
              <Form.Check
                inline
                className="ml-5"
                label="Controlada"
                checked={tipoAtual === 'Controlada'}
                name="groupRec"
                type="radio"
                id="Controlada"
                onChange={() => handleAlterarTipoReceita('Controlada')}
              />
            </div>
            {isDoctorLoggedIn && (
              <>
                <Button
                  variant="outline-success"
                  size="sm"
                  onClick={printOrientacaoMarevan}
                >
                  Orientação Marevan
                </Button>{' '}
                <Button
                  variant="outline-success"
                  size="sm"
                  onClick={printOrientacaoDOC}
                >
                  Orientação DOC
                </Button>
                <div>
                  <Form.Check
                    inline
                    className="ml-5"
                    label="Uso oral contínuo"
                    defaultChecked={usoReceita === 'oral-continuo'}
                    name="uso-receita"
                    type="radio"
                    onClick={() => setUsoReceita('oral-continuo')}
                  />
                  <Form.Check
                    inline
                    className="ml-5"
                    label="Uso externo contínuo"
                    defaultChecked={usoReceita === 'oral-externo'}
                    name="uso-receita"
                    type="radio"
                    onClick={() => setUsoReceita('oral-externo')}
                  />
                </div>
              </>
            )}
          </div>
          <hr></hr>
          <Form.Row>
            <Col sm>
              <Form.Group controlId="editMedicamentosReceita">
                <strong>
                  Descrição da Receita{' '}
                  {currentReceita && currentReceita.codReceita} -{' '}
                  {currentReceita && currentReceita.dataRec}
                </strong>
                <Form.Control
                  as="textarea"
                  readOnly={isDoctorLoggedIn ? false : true}
                  style={{ fontSize: '12px' }}
                  rows={20}
                  value={currentReceita?.descricao}
                  onChange={e =>
                    setReceita({
                      ...currentReceita,
                      descricao: e.target.value,
                    })
                  }
                />
              </Form.Group>
            </Col>
            {renderTreeMedicamento && (
              <SimpleTreeView
                aria-label="rich object"
                defaultExpanded={['root']}
                slots={{
                  expandIcon: FaArrowRight,
                  collapseIcon: FaAngleDown,
                }}
                onSelectedItemsChange={(event, nodeId) =>
                  handleSelectMedicamentoReceita(
                    event,
                    nodeId,
                    'editMedicamentosReceita'
                  )
                }
              >
                {renderTreeMedicamento(dbmedicamentos)}
              </SimpleTreeView>
            )}
          </Form.Row>
        </Form>

        {isDoctorLoggedIn && typeof setAssinaturaDigital === 'function' && (
          <Form.Check
            type="checkbox"
            checked={!!assinaturaDigital}
            label="Assinatura Digital (Vidaas)"
            onChange={() => setAssinaturaDigital(prev => !prev)}
          />
        )}

        {isDoctorLoggedIn && (
          <Form.Check
            type="checkbox"
            checked={!!assinaturaEletronica}
            label="Sem Assinatura Eletrônica"
            onChange={() => setAssinaturaEletronica(prev => !prev)}
          />
        )}

        <Card.Footer className="text-center">
          {isDoctorLoggedIn && (
            <Button onClick={handleGravarReceita} disabled={isSavingReceita}>
              {isSavingReceita ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />{' '}
                  Gravando...
                </>
              ) : (
                'Gravar'
              )}
            </Button>
          )}{' '}
          <Button
            variant="warning"
            onClick={() => handleImprimirReceita(currentReceita, false)}
          >
            Imprimir
          </Button>{' '}
          <Button
            variant="info"
            onClick={() => handleImprimirReceita(currentReceita, true)}
          >
            Timbrado
          </Button>{' '}
          {isDoctorLoggedIn && (
            <Button variant="outline-success" onClick={enviarPorWpp}>
              <FaWhatsapp /> Enviar PDF
            </Button>
          )}{' '}
          <Button variant="danger" onClick={closeModal}>
            Fechar
          </Button>
          {signing && (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
              <span className=""> Assinando Receita...</span>
            </>
          )}
        </Card.Footer>
      </Card>

      <div style={{ display: 'none' }}>
        <OrientacaoMarevan ref={orientacaoRef} />
      </div>
      <div style={{ display: 'none' }}>
        <OrientacaoDOC ref={orientacaoDOCRef} />
      </div>

      <Modal
        show={showModalRepetir}
        onHide={() => setShowModalRepetir(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Repetir Receita — Tipo de Receituário</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            Selecione o tipo de receituário para repetir esta receita:
          </p>
          <Form.Group className="mb-0">
            <Form.Check
              type="radio"
              id="repetir-receita-normal"
              name="tipoRepeticaoModal"
              label="Receita Normal"
              checked={tipoRepeticao === 'Normal'}
              onChange={() => setTipoRepeticao('Normal')}
              className="mb-2"
            />
            <Form.Check
              type="radio"
              id="repetir-receita-controlada"
              name="tipoRepeticaoModal"
              label="Receita Controlada"
              checked={tipoRepeticao === 'Controlada'}
              onChange={() => setTipoRepeticao('Controlada')}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowModalRepetir(false)}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={() => handleConfirmarRepetirReceita(tipoRepeticao)}
          >
            Confirmar Repetição
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
